import { useCallback, useEffect, useRef, useState } from "react";
import type { CueScript } from "../types";
export interface RehearsalController {
  voices: SpeechSynthesisVoice[];
  voiceURI: string;
  rate: number;
  pitch: number;
  speaking: boolean;
  paused: boolean;
  supported: boolean;
  error: string | null;
  speak: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVoiceURI: (value: string) => void;
  setRate: (value: number) => void;
  setPitch: (value: number) => void;
}
interface SpeechSession {
  id: number;
  paused: boolean;
  waiting: boolean;
  utterance: SpeechSynthesisUtterance | null;
  next: () => void;
}

function preferredVoice(voices: SpeechSynthesisVoice[]) {
  return voices.find((voice) =>
    voice.name.trim().toLowerCase() === "google uk english female" &&
    voice.lang.replaceAll("_", "-").toLowerCase() === "en-gb",
  );
}

function speechErrorMessage(error: string): string {
  if (error === "not-allowed")
    return "Reading aloud was blocked. Click Play to try again, or check the browser’s audio permission.";
  return `Could not read aloud (${error}). Choose another voice in Studio controls → Rehearsal, then try again.`;
}

export function useRehearsal(
  beforeSpeak: () => void,
  script?: Pick<CueScript, "id" | "html">,
): RehearsalController {
  const supported =
    typeof window !== "undefined" && !!window.speechSynthesis &&
    typeof window.SpeechSynthesisUtterance === "function";
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  // null follows the app default as Chrome loads its voices asynchronously.
  // An explicit choice (including "" for System default) stays selected.
  const [selectedVoiceURI, setVoiceURI] = useState<string | null>(null);
  const voiceURI = selectedVoiceURI ?? preferredVoice(voices)?.voiceURI ?? "";
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  // Retain the current utterance while speaking, and guard late browser events.
  const session = useRef<SpeechSession | null>(null);
  const stop = useCallback(() => {
    generation.current++;
    session.current = null;
    if (supported) window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  }, [supported]);
  useEffect(() => {
    if (session.current) stop();
    setError(null);
  }, [script?.id, script?.html, stop]);
  useEffect(() => {
    if (!supported) return;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => {
      generation.current++;
      session.current = null;
      window.speechSynthesis.removeEventListener("voiceschanged", load);
      window.speechSynthesis.cancel();
    };
  }, [supported]);
  const speak = useCallback(
    (text: string) => {
      if (!supported || !text.trim()) return;
      stop();
      setError(null);
      const id = generation.current;
      const fail = (reason: string) => {
        if (id !== generation.current) return;
        generation.current++;
        session.current = null;
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setPaused(false);
        if (reason !== "interrupted" && reason !== "canceled")
          setError(speechErrorMessage(reason));
      };
      const chunks = text.match(/[^.!?\n]+[.!?\n]*/gu) || [text];
      let cursor = 0;
      let readingVoice: SpeechSynthesisVoice | null = null;
      const next = () => {
        const current = session.current;
        if (id !== generation.current || current?.id !== id) return;
        if (current.paused) {
          current.waiting = true;
          return;
        }
        current.waiting = false;
        const chunk = chunks[cursor++];
        if (!chunk) {
          session.current = null;
          setSpeaking(false);
          setPaused(false);
          return;
        }
        try {
          const utterance = new SpeechSynthesisUtterance(chunk);
          current.utterance = utterance;
          utterance.voice = readingVoice;
          utterance.rate = Math.max(0.25, Math.min(3, rate));
          utterance.pitch = Math.max(0, Math.min(2, pitch));
          utterance.onend = () => {
            if (session.current?.utterance !== utterance) return;
            session.current.utterance = null;
            next();
          };
          utterance.onerror = (event) => {
            if (session.current?.utterance === utterance) fail(event.error);
          };
          window.speechSynthesis.speak(utterance);
        } catch (cause) {
          fail(cause instanceof Error ? cause.message : "speech unavailable");
        }
      };
      try {
        // Query once at Play, too: Chrome can populate voices before delivering
        // voiceschanged. Keep one voice for the complete reading.
        const available = window.speechSynthesis.getVoices();
        readingVoice = selectedVoiceURI === null
          ? preferredVoice(available) ?? null
          : available.find((voice) => voice.voiceURI === selectedVoiceURI) ?? null;
        beforeSpeak();
        // Chromium can keep the global synthesizer paused after cancel().
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      } catch (cause) {
        fail(cause instanceof Error ? cause.message : "speech unavailable");
        return;
      }
      session.current = { id, paused: false, waiting: false, utterance: null, next };
      setSpeaking(true);
      setPaused(false);
      next();
    },
    [supported, stop, beforeSpeak, selectedVoiceURI, rate, pitch],
  );
  const pause = () => {
    const current = session.current;
    if (!supported || !current || current.paused) return;
    current.paused = true;
    window.speechSynthesis.pause();
    setPaused(true);
  };
  const resume = () => {
    const current = session.current;
    if (!supported || !current?.paused) return;
    current.paused = false;
    window.speechSynthesis.resume();
    setPaused(false);
    if (current.waiting) current.next();
  };
  return {
    voices,
    voiceURI,
    rate,
    pitch,
    speaking,
    paused,
    supported,
    error,
    speak,
    pause,
    resume,
    stop,
    setVoiceURI,
    setRate,
    setPitch,
  };
}
